import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatValue } from "@/lib/api";
import { Loader2, GraduationCap, ListOrdered, Info } from "lucide-react";
import { useFormat } from "@/contexts/FormatContext";
import DmeTag from "@/components/DmeTag";

const TIER_LABELS = { 1: "Tier 1 · Elite", 2: "Tier 2 · Cornerstone", 3: "Tier 3 · Starter", 4: "Tier 4 · Depth", 5: "Tier 5 · Flier" };
const DEVY_TIER_LABELS = { "Elite": "Elite", "High-End": "High-End", "Mid": "Mid", "Developmental": "Developmental" };
const DEVY_TIER_ORDER = ["Elite", "High-End", "Mid", "Developmental"];

function PicksPanel({ picks, season }) {
  return (
    <div>
      <div className="text-xs tracking-widest text-[var(--text-3)] mb-2 flex items-center gap-1.5">{season} ROOKIE PICK VALUES <DmeTag /></div>
      <div className="card-bordered overflow-hidden" data-testid="rookie-picks">
        {picks.map((p, i) => (
          <div key={i} className="flex justify-between px-4 py-2 text-sm border-b border-[var(--border)] last:border-0">
            <span className="text-[var(--text-2)]">{p.label}</span>
            <span className="tabular text-[var(--accent)]">{formatValue(p.value)}</span>
          </div>
        ))}
        {picks.length === 0 && <div className="p-4 text-xs text-[var(--text-3)]">Loading pick values…</div>}
      </div>
    </div>
  );
}

function DevyBoard({ prospects, picks, season }) {
  const tiers = DEVY_TIER_ORDER.filter(t => prospects.some(p => p.tier_label === t));
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-5">
        {tiers.map(t => (
          <div key={t} data-testid={`devy-tier-${t}`}>
            <div className="text-xs tracking-widest text-[var(--accent)] mb-2">{DEVY_TIER_LABELS[t] || t}</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {prospects.filter(p => p.tier_label === t).map(p => (
                <div key={`${p.name}-${p.rank}`}
                  className="card-bordered p-3 flex items-center justify-between"
                  data-testid={`devy-${p.rank}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono text-[var(--text-3)] text-sm w-6">{p.rank}</span>
                    <div className="min-w-0">
                      <div className="truncate text-sm">{p.name}</div>
                      <div className="text-[10px] tracking-widest text-[var(--text-3)]">{p.position} · {p.school}{p.class_year ? ` · ${p.class_year}` : ""}</div>
                    </div>
                  </div>
                  <span className="text-[10px] tracking-widest text-[var(--accent)] border border-[var(--accent)]/40 rounded px-1.5 py-0.5 whitespace-nowrap">{p.tier_label}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <PicksPanel picks={picks} season={season} />
    </div>
  );
}

function RookieBoard({ rookies, picks, season }) {
  const tiers = [...new Set(rookies.map(r => r.tier))].sort();
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-5">
        {tiers.map(t => (
          <div key={t} data-testid={`rookie-tier-${t}`}>
            <div className="text-xs tracking-widest text-[var(--accent)] mb-2">{TIER_LABELS[t] || `Tier ${t}`}</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {rookies.filter(r => r.tier === t).map(r => (
                <Link to={`/app/players/${r.player_id}`} key={r.player_id}
                  className="card-bordered p-3 flex items-center justify-between hover:border-[var(--accent)]/40 transition-colors"
                  data-testid={`rookie-${r.player_id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono text-[var(--text-3)] text-sm w-6">{r.rank}</span>
                    <div className="min-w-0">
                      <div className="truncate text-sm">{r.name}</div>
                      <div className="text-[10px] tracking-widest text-[var(--text-3)]">{r.position} · {r.team}{r.age ? ` · ${Math.round(r.age)}y` : ""}</div>
                    </div>
                  </div>
                  <span className="tabular text-[var(--accent)] text-sm">{formatValue(r.value)}</span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
      <PicksPanel picks={picks} season={season} />
    </div>
  );
}

function AdpBoard({ adp }) {
  return (
    <div className="card-bordered overflow-hidden">
      <table className="w-full text-sm tabular" data-testid="adp-table">
        <thead className="text-[10px] tracking-widest text-[var(--text-3)]">
          <tr className="border-b border-[var(--border)]">
            <th className="text-left p-3 w-16">ADP</th>
            <th className="text-left p-3">Player</th>
            <th className="text-left p-3">Pos</th>
            <th className="text-right p-3"><span className="inline-flex items-center gap-1.5 justify-end">DME Value <DmeTag /></span></th>
          </tr>
        </thead>
        <tbody>
          {adp.map(a => (
            <tr key={a.player_id} className="border-b border-[var(--border)] hover:bg-white/[0.03]" data-testid={`adp-${a.player_id}`}>
              <td className="p-3 font-mono text-[var(--accent)]">{a.adp}</td>
              <td className="p-3"><Link to={`/app/players/${a.player_id}`} className="hover:text-[var(--accent)]">{a.name}</Link></td>
              <td className="p-3 text-[var(--text-2)]">{a.position} · {a.team}</td>
              <td className="p-3 text-right">{formatValue(a.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DraftBoard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("rookies");
  const { format } = useFormat();

  useEffect(() => {
    setLoading(true);
    api.get("/draft/board").then(r => setData(r.data)).finally(() => setLoading(false));
  }, [format]);

  return (
    <div className="space-y-6 fade-up">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[var(--accent)] text-xs tracking-widest mb-1">// DRAFT</div>
          <h1 className="font-display text-4xl font-bold" data-testid="draft-heading">Rookie Draft & ADP</h1>
          <p className="text-[var(--text-2)] mt-2">Live rookie rankings, pick values, and startup ADP — powered by the Dynasty Metrics Value Engine, refreshed daily.</p>
          {data?.note && (
            <div className="mt-3 inline-flex items-start gap-2 text-xs text-[var(--text-2)] border border-[var(--accent)]/30 bg-[var(--accent)]/5 rounded-md px-3 py-2 max-w-2xl" data-testid="draft-roll-note">
              <Info className="w-3.5 h-3.5 mt-0.5 text-[var(--accent)] shrink-0" />
              <span>{data.note}</span>
            </div>
          )}
        </div>
        <div className="inline-flex border border-[var(--border)] rounded-md overflow-hidden">
          <button onClick={() => setTab("rookies")} className={`px-4 py-2 text-sm inline-flex items-center gap-2 ${tab === "rookies" ? "bg-[var(--accent)] text-black" : "text-[var(--text-2)] hover:bg-white/5"}`} data-testid="draft-tab-rookies">
            <GraduationCap className="w-4 h-4" /> Rookie Board
          </button>
          <button onClick={() => setTab("adp")} className={`px-4 py-2 text-sm inline-flex items-center gap-2 ${tab === "adp" ? "bg-[var(--accent)] text-black" : "text-[var(--text-2)] hover:bg-white/5"}`} data-testid="draft-tab-adp">
            <ListOrdered className="w-4 h-4" /> Startup ADP
          </button>
        </div>
      </header>

      {loading || !data ? (
        <div className="card-bordered p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>
      ) : tab === "rookies" ? (
        <RookieBoard rookies={data.rookies} picks={data.rookie_picks} season={data.rookie_season} />
      ) : (
        <AdpBoard adp={data.adp} />
      )}
    </div>
  );
}
